import { useEffect, useState } from "react";
import { ModelRenderer } from "../ModelRenderer";
import { URDFRobot } from "urdf-loader";
import { Model, MotionMapperType, QRMotionMapper } from "../../models/Model";
import { MREvents } from "../MREvents";
import { Box, Button, TextField, Typography } from "@mui/material";
import { QRAnimator, QRAnimatorStates } from "../../visuals/QRAnimator";
import { PropValueLine } from "../common/PropValueLine";

const defaultMapper: QRMotionMapper = {
  walkingHeight: 0,
  thighLength: 0.2,
  shinLength: 0.2,
  stride: 0.1,
  type: MotionMapperType.QuadrupedRobot,
  jointMap: [],
  invertedY: false,
};

export function QRAnimationPropsPanel({
  mr,
  robot,
}: {
  mr: ModelRenderer;
  robot: URDFRobot;
}) {
  // const [mapper, setMapper] = useState<QRMotionMapper>();
  const [cfg, setCfg] = useState<QRMotionMapper>(defaultMapper);
  useEffect(() => {
    if (!robot) {
      return;
    }
    const modelUpdatesListener = (model: Model) => {
      if (model.motionMapper.type === MotionMapperType.QuadrupedRobot) {
        setCfg(model.motionMapper as QRMotionMapper);
        mr.animator = new QRAnimator(mr.loader);
      } else {
        setCfg(defaultMapper);
      }
    };
    mr.onEvent(MREvents.ModelUpdated, modelUpdatesListener);
    modelUpdatesListener(mr.loader.model);
    return () => {
      mr.removeListener(MREvents.ModelUpdated, modelUpdatesListener);
    };
  }, [robot]);

  const mergeMapper = (mapper: Partial<QRMotionMapper>) => {
    const updatedMapper = { ...cfg, ...mapper };
    setCfg(updatedMapper);
    const model = mr.loader.model;
    model.motionMapper = updatedMapper as QRMotionMapper;
    mr.animator = new QRAnimator(mr.loader);
  };

  return (
    <>
      <PropValueLine
        labelWidth={200}
        label="Walking height"
        value={
          <TextField
            type="number"
            value={cfg.walkingHeight}
            onChange={(e) =>
              mergeMapper({ walkingHeight: parseFloat(e.target.value) })
            }
            variant="standard"
            size="small"
            sx={{ width: "80px" }}
          />
        }
      />
      <PropValueLine
        labelWidth={200}
        label="Thigh length"
        value={
          <TextField
            type="number"
            value={cfg.thighLength}
            onChange={(e) =>
              mergeMapper({ thighLength: parseFloat(e.target.value) })
            }
            variant="standard"
            size="small"
            sx={{ width: "80px" }}
          />
        }
      />
      <PropValueLine
        labelWidth={200}
        label="Shin length"
        value={
          <TextField
            type="number"
            value={cfg.shinLength}
            onChange={(e) =>
              mergeMapper({ shinLength: parseFloat(e.target.value) })
            }
            variant="standard"
            size="small"
            sx={{ width: "80px" }}
          />
        }
      />
      <PropValueLine
        labelWidth={200}
        label="Stride"
        value={
          <TextField
            type="number"
            value={cfg.stride}
            onChange={(e) =>
              mergeMapper({ stride: parseFloat(e.target.value) })
            }
            variant="standard"
            size="small"
            sx={{ width: "80px" }}
          />
        }
      />

      <Box>
        <Button
          size="small"
          variant="outlined"
          color="error"
          onClick={() => {
            const a = mr.animator as QRAnimator;
            a.autoConfig();
            setCfg({ ...a.mapper });
          }}
        >
          {" "}
          AUTO{" "}
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="error"
          onClick={() => {
            const a = mr.animator as QRAnimator;
            a.neutralize();
          }}
        >
          {" "}
          DEFAULTS{" "}
        </Button>

        <Button
          size="small"
          variant="outlined"
          color="error"
          onClick={() => {
            const a = mr.animator as QRAnimator;
            if (a.state === QRAnimatorStates.Walking) {
              a.stop();
            } else {
              a.walk();
            }
          }}
        >
          {" "}
          {(mr.animator as QRAnimator).state === QRAnimatorStates.Walking
            ? "Stop"
            : "Walk"}
        </Button>
      </Box>
    </>
  );
}
